import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Edit2, Copy, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { sortCustomizations } from '@/lib/customizationUtils';
import { isTestLineItem } from '@/lib/orderItemFilters';

const FABRIC_SUMMARY_ORDER = ['main', 'contrast', 'lining'];
const FABRIC_SUMMARY_LABELS = {
  main: 'Main',
  contrast: 'Contrast',
  lining: 'Lining',
};

const normalizeFabricSelectorType = (option = {}, fallbackOptionName = '') => {
  const selectorType = String(option?.fabric_selector_type || '').toLowerCase().replace('_fabric', '').trim();
  if (['main', 'lining', 'contrast'].includes(selectorType)) {
    return selectorType;
  }

  const optionName = String(option?.name || fallbackOptionName || '').toLowerCase().replace(/\s+/g, ' ').trim();
  if (optionName.includes('main fabric id') || optionName.includes('main/base fabric id') || optionName.includes('base fabric id')) {
    return 'main';
  }
  if (optionName.includes('contrast fabric id')) {
    return 'contrast';
  }
  if (optionName.includes('lining fabric id')) {
    return 'lining';
  }

  return null;
};

// Helper function to format currency
const formatCurrency = (amount) => {
 return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
};

const LineItemRow = ({ 
 item, 
 index, 
 selected, 
 onSelect, 
 onEdit, 
 onDelete, 
 onDuplicate,
 onBasePriceChange,
 orderNumber,
 linkedGarment,
 productCategories // NEW: Pass product categories for sorting
}) => {
 const [isOpen, setIsOpen] = useState(false);
 const [editingBasePrice, setEditingBasePrice] = useState(false);
 const [basePriceValue, setBasePriceValue] = useState(item.base_price);
 
 // Sort and group customizations by category with proper display order
 const groupedCustomizations = useMemo(() => {
 if (!item.customizations?.length) return [];
 
 // Use snapshot schema if available, otherwise fall back to productCategories
 const schemaToUse = item.snapshot_customization_schema?.categories || productCategories;
 
 // Sort customizations first
 const sortedCustomizations = sortCustomizations(item.customizations, schemaToUse);
 
 // Group by category while preserving category metadata
 const grouped = sortedCustomizations.reduce((acc, c) => {
 const categoryName = c.category_name || 'General';
 const category = schemaToUse?.find(cat => cat.name === categoryName);
 
 if (!acc[categoryName]) {
 acc[categoryName] = {
 name: categoryName,
 display_order: category?.display_order ?? 999,
 options: []
 };
 }
 acc[categoryName].options.push(c);
 return acc;
 }, {});
 
 // Convert to array and sort by display_order
 return Object.values(grouped).sort((a, b) => a.display_order - b.display_order);
 }, [item.customizations, item.snapshot_customization_schema, productCategories]);

 const inlineFabricSummary = useMemo(() => {
  if (!item.customizations?.length) return [];

  const schemaToUse = item.snapshot_customization_schema?.categories || productCategories;
  const allOptions = (schemaToUse || []).flatMap((category) => category.options || []);
  const buckets = {
    main: [],
    contrast: [],
    lining: [],
  };

  (item.customizations || []).forEach((customization) => {
    if (!customization?.selected_value) return;
    const option = allOptions.find((candidate) => candidate.option_id === customization.option_id);
    const normalizedType = normalizeFabricSelectorType(option, customization.option_name);
    if (!normalizedType || !buckets[normalizedType]) return;
    const value = String(customization.selected_value).trim();
    if (!value || buckets[normalizedType].includes(value)) return;
    buckets[normalizedType].push(value);
  });

  return FABRIC_SUMMARY_ORDER
    .filter((key) => buckets[key].length > 0)
    .map((key) => ({
      key,
      label: FABRIC_SUMMARY_LABELS[key],
      value: buckets[key].join(', '),
    }));
 }, [item.customizations, item.snapshot_customization_schema, productCategories]);

 const customizationCount = item.customizations?.length || 0;

 const displayLineItemRef = useMemo(() => {
  const garmentNo = linkedGarment?.garment_no;
  if (!garmentNo || !orderNumber) {
   return item.line_item_ref;
  }

  let suffix = '';
  if (garmentNo.startsWith(`${orderNumber}-`)) {
   suffix = garmentNo.slice(orderNumber.length + 1);
  } else if (garmentNo.includes('-')) {
   suffix = garmentNo.split('-').pop();
  }

  return suffix ? `${orderNumber}-${suffix}` : item.line_item_ref;
 }, [linkedGarment?.garment_no, orderNumber, item.line_item_ref]);

 const handleBasePriceSubmit = () => {
 const newBasePrice = parseFloat(basePriceValue);
 if (!isNaN(newBasePrice) && newBasePrice >= 0 && onBasePriceChange) {
 onBasePriceChange(item.line_item_id, newBasePrice);
 }
 setEditingBasePrice(false);
 };

 return (
 <div className={`border border-slate-200 rounded-lg mb-3 overflow-hidden ${selected ? 'ring-2 ring-orange-400' : ''}`}>
 {/* Header Section */}
 <div className="bg-white p-4">
 <div className="flex items-start gap-4">
 <Checkbox
 checked={selected}
 onCheckedChange={onSelect}
 className="mt-1"
 />
 <div className="flex-1 min-w-0">
 {/* Product Name and Reference */}
 <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
 <span className="font-semibold text-lg text-[#0F172A]">{item.product_name}</span>
 {isTestLineItem(item) && (
 <Badge className="bg-orange-100 text-orange-800 border-orange-200" data-testid={`line-item-test-badge-${item.line_item_id}`}>
 Test
 </Badge>
 )}
 <Badge variant="outline" className="font-mono text-xs bg-slate-100 text-slate-700">
 {displayLineItemRef}
 </Badge>
 <span className="text-sm text-slate-500">Qty: {item.quantity}</span>
  {inlineFabricSummary.length > 0 && (
   <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500" data-testid={`line-item-inline-fabric-summary-${item.line_item_id}`}>
    {inlineFabricSummary.map((fabric) => (
     <span key={fabric.key} data-testid={`line-item-inline-fabric-${fabric.key}-${item.line_item_id}`}>
      <span className="font-medium text-slate-600">{fabric.label}:</span> {fabric.value}
     </span>
    ))}
   </div>
  )}
 </div>
 
 {/* Price Information */}
 <div className="flex items-center gap-6 text-sm">
 <div className="flex items-center gap-2">
 <span className="text-slate-500">Base Price:</span>
 {editingBasePrice ? (
 <div className="flex items-center gap-1">
 <span className="text-slate-600">$</span>
 <input
 type="number"
 value={basePriceValue}
 onChange={(e) => setBasePriceValue(e.target.value)}
 onBlur={handleBasePriceSubmit}
 onKeyDown={(e) => e.key === 'Enter' && handleBasePriceSubmit()}
 className="w-20 h-6 px-2 text-sm text-slate-700 bg-white border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
 autoFocus
 />
 </div>
 ) : (
 <span 
 className="font-medium text-slate-900 cursor-pointer hover:text-orange-600 underline decoration-dashed underline-offset-2"
 onClick={() => setEditingBasePrice(true)}
 title="Click to edit base price"
 >
 {formatCurrency(item.base_price)}
 </span>
 )}
 </div>
 <div className="flex items-center gap-2">
 <span className="text-slate-500">Line Total:</span>
 <span className="font-semibold text-orange-600">
 {formatCurrency(item.line_item_price * item.quantity)}
 </span>
 </div>
 </div>
 </div>
 
 {/* Action Buttons */}
 <div className="flex items-center gap-1">
 <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit} title="Edit customizations">
 <Edit2 className="w-4 h-4" />
 </Button>
 <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onDuplicate} title="Duplicate item">
 <Copy className="w-4 h-4" />
 </Button>
 <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={onDelete} title="Delete item">
 <Trash2 className="w-4 h-4" />
 </Button>
 </div>
 </div>
 </div>
 
 {/* View Customizations Toggle - Orange Bar */}
 {customizationCount > 0 && (
 <div 
 className="flex items-center justify-between px-4 py-2 bg-orange-50 border-t border-orange-200 cursor-pointer hover:bg-orange-100 transition-colors"
 onClick={() => setIsOpen(!isOpen)}
 >
 <span className="text-sm font-medium text-orange-800">
 View Customizations ({customizationCount})
 </span>
 {isOpen ? <ChevronDown className="w-4 h-4 text-orange-600" /> : <ChevronRight className="w-4 h-4 text-orange-600" />}
 </div>
 )}
 
 {/* Customizations Content - Grouped by Category */}
 {isOpen && customizationCount > 0 && (
 <div className="bg-slate-50 border-t border-slate-200">
 {groupedCustomizations.map((categoryGroup, catIndex) => (
 <div key={categoryGroup.name} className={catIndex > 0 ? 'border-t border-slate-200' : ''}>
 {/* Category Header */}
 <div className="px-4 py-2 bg-slate-100">
 <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
 {categoryGroup.name}
 </span>
 </div>
 {/* Options Grid */}
 <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
 {categoryGroup.options.map((opt, optIndex) => (
 <div key={optIndex} className="flex items-baseline gap-2">
 <span className="text-sm text-slate-600 whitespace-nowrap">{opt.option_name}:</span>
 <span className="text-sm font-medium text-slate-900 whitespace-pre-line">
 {opt.selected_value}
 {opt.price_impact > 0 && (
 <span className="text-orange-600 ml-1">(+${opt.price_impact})</span>
 )}
 </span>
 </div>
 ))}
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 );
};

export default LineItemRow;
export { formatCurrency };
